import { useMemo, useState } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { ArrowLeftRight, Loader2, Search, TrendingDown, TrendingUp, X } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Pagination } from '@/components/Pagination';
import { useFinance } from '@/contexts/FinanceContext';
import { useCurrentClient } from '@/hooks/useCurrentClient';
import type { TransactionType, TransactionStatus } from '@/types/finance';

const fmtBrl = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

interface PortalTxFilters {
  search: string;
  type: TransactionType | 'all';
  status: TransactionStatus | 'all';
  fromDate: string;
  toDate: string;
}

const DEFAULT_FILTERS: PortalTxFilters = {
  search: '',
  type: 'all',
  status: 'all',
  fromDate: '',
  toDate: '',
};

const PAGE_SIZE = 30;

const STATUS_LABEL: Record<TransactionStatus, string> = {
  pending: 'Pendente',
  completed: 'Pago',
  overdue: 'Vencido',
};

const STATUS_BADGE: Record<TransactionStatus, string> = {
  pending: 'bg-amber-100 text-amber-700',
  completed: 'bg-green-100 text-green-700',
  overdue: 'bg-red-100 text-red-700',
};

export function PortalTransactionsPage() {
  const { clientId, client } = useCurrentClient();
  const { transactions, accounts, categories, isLoading } = useFinance();
  const [filters, setFilters] = useState<PortalTxFilters>(DEFAULT_FILTERS);
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const term = filters.search.trim().toLowerCase();
    const from = filters.fromDate ? new Date(`${filters.fromDate}T00:00:00`) : null;
    const to = filters.toDate ? new Date(`${filters.toDate}T23:59:59`) : null;

    return transactions
      .filter((t) => {
        if (filters.type !== 'all' && t.type !== filters.type) return false;
        if (filters.status !== 'all' && t.status !== filters.status) return false;
        if (term && !t.description.toLowerCase().includes(term)) return false;
        if (from && t.dueDate < from) return false;
        if (to && t.dueDate > to) return false;
        return true;
      })
      .sort((a, b) => b.dueDate.getTime() - a.dueDate.getTime());
  }, [transactions, filters]);

  const totals = useMemo(() => {
    let income = 0;
    let expense = 0;
    for (const t of filtered) {
      if (t.type === 'income') income += Number(t.amount);
      else if (t.type === 'expense') expense += Number(t.amount);
    }
    return { income, expense, net: income - expense };
  }, [filtered]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  const hasActiveFilters =
    filters.search.trim() !== '' ||
    filters.type !== 'all' ||
    filters.status !== 'all' ||
    filters.fromDate !== '' ||
    filters.toDate !== '';

  if (!clientId) {
    return (
      <Card>
        <CardContent className="p-12 text-center text-muted-foreground">
          <ArrowLeftRight className="mx-auto mb-3 h-10 w-10 opacity-40" />
          <p className="text-sm">Sem cliente selecionado.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-brand text-2xl font-bold text-foreground">Lançamentos</h1>
        <p className="text-sm text-muted-foreground">
          {client?.name ? `${client.name} · ` : ''}consulta dos lançamentos da clínica
        </p>
      </div>

      {/* Resumo do período filtrado */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Receitas
            </p>
            <p className="mt-1 text-2xl font-bold tabular-nums text-income">
              {fmtBrl(totals.income)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Despesas
            </p>
            <p className="mt-1 text-2xl font-bold tabular-nums text-expense">
              {fmtBrl(totals.expense)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Saldo do período
            </p>
            <p
              className={`mt-1 text-2xl font-bold tabular-nums ${
                totals.net >= 0 ? 'text-income' : 'text-expense'
              }`}
            >
              {fmtBrl(totals.net)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-1.5 lg:col-span-2">
              <Label className="text-xs">Buscar</Label>
              <div className="relative">
                <Search
                  size={14}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                />
                <Input
                  placeholder="Descrição…"
                  value={filters.search}
                  onChange={(e) => {
                    setFilters((f) => ({ ...f, search: e.target.value }));
                    setPage(1);
                  }}
                  className="pl-9"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Tipo</Label>
              <Select
                value={filters.type}
                onValueChange={(v) => {
                  setFilters((f) => ({ ...f, type: v as TransactionType | 'all' }));
                  setPage(1);
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="income">Receitas</SelectItem>
                  <SelectItem value="expense">Despesas</SelectItem>
                  <SelectItem value="transfer">Transferências</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Status</Label>
              <Select
                value={filters.status}
                onValueChange={(v) => {
                  setFilters((f) => ({ ...f, status: v as TransactionStatus | 'all' }));
                  setPage(1);
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="pending">Pendente</SelectItem>
                  <SelectItem value="completed">Pago</SelectItem>
                  <SelectItem value="overdue">Vencido</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2 md:col-span-2">
              <div className="space-y-1.5">
                <Label className="text-xs">Vence de</Label>
                <Input
                  type="date"
                  value={filters.fromDate}
                  onChange={(e) => {
                    setFilters((f) => ({ ...f, fromDate: e.target.value }));
                    setPage(1);
                  }}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Até</Label>
                <Input
                  type="date"
                  value={filters.toDate}
                  onChange={(e) => {
                    setFilters((f) => ({ ...f, toDate: e.target.value }));
                    setPage(1);
                  }}
                />
              </div>
            </div>
          </div>
          {hasActiveFilters && (
            <div className="flex justify-end border-t border-border pt-3">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setFilters(DEFAULT_FILTERS);
                  setPage(1);
                }}
                className="h-7 text-xs"
              >
                <X size={12} className="mr-1" />
                Limpar filtros
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tabela */}
      {isLoading ? (
        <Card>
          <CardContent className="flex items-center justify-center p-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </CardContent>
        </Card>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center text-muted-foreground">
            <ArrowLeftRight className="mx-auto mb-3 h-10 w-10 opacity-40" />
            <p className="text-sm">
              {hasActiveFilters
                ? 'Nenhum lançamento corresponde aos filtros.'
                : 'Sem lançamentos ainda.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-secondary/40 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    <th className="px-3 py-2 text-left">Data</th>
                    <th className="px-3 py-2 text-left">Descrição</th>
                    <th className="px-3 py-2 text-left">Categoria</th>
                    <th className="px-3 py-2 text-left">Conta</th>
                    <th className="px-3 py-2 text-left">Status</th>
                    <th className="px-3 py-2 text-right">Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {paged.map((t) => {
                    const cat = categories.find((c) => c.id === t.categoryId);
                    const acc = accounts.find((a) => a.id === t.accountId);
                    const isIncome = t.type === 'income';
                    const Icon = isIncome ? TrendingUp : TrendingDown;
                    return (
                      <tr key={t.id} className="border-b border-border/50">
                        <td className="px-3 py-2 text-xs">
                          {format(t.dueDate, 'dd/MM/yyyy', { locale: ptBR })}
                        </td>
                        <td className="max-w-xs px-3 py-2">
                          <div className="flex items-center gap-2">
                            <Icon size={14} className={isIncome ? 'text-income' : 'text-expense'} />
                            <span className="truncate text-sm" title={t.description}>
                              {t.description}
                            </span>
                          </div>
                        </td>
                        <td className="px-3 py-2 text-xs">
                          {cat ? (
                            <span className="inline-flex items-center gap-1.5">
                              <span
                                className="h-2 w-2 rounded-full"
                                style={{ backgroundColor: cat.color }}
                              />
                              {cat.name}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs">{acc?.name ?? '—'}</td>
                        <td className="px-3 py-2">
                          <span
                            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_BADGE[t.status]}`}
                          >
                            {STATUS_LABEL[t.status]}
                          </span>
                        </td>
                        <td
                          className={`px-3 py-2 text-right text-sm font-semibold tabular-nums ${
                            isIncome ? 'text-income' : 'text-expense'
                          }`}
                        >
                          {isIncome ? '+' : '−'} {fmtBrl(Number(t.amount))}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {totalPages > 1 && (
        <div className="flex flex-col items-center gap-2 pt-2">
          <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
          <p className="text-xs text-muted-foreground">
            {filtered.length} lançamento{filtered.length === 1 ? '' : 's'} · página {page} de{' '}
            {totalPages}
          </p>
        </div>
      )}
    </div>
  );
}
